import { BlogPost, BlogCategory } from '../types/blog';

// SSG-optimized blog utilities
// Fetches pre-rendered JSON data instead of parsing markdown at runtime

const BLOG_DATA_BASE = '/blog-data';

// Cache for blog data
let blogIndexCache: BlogPost[] | null = null;
const postCache = new Map<string, BlogPost>();

export async function getAllPostsSSG(): Promise<BlogPost[]> {
  // Return cached if available
  if (blogIndexCache) {
    return blogIndexCache;
  }

  try {
    const response = await fetch(`${BLOG_DATA_BASE}/index.json`);

    if (!response.ok) {
      console.error(`Failed to fetch blog index: ${response.status}`);
      return [];
    }

    const posts = await response.json();

    // Transform reading time if needed
    const transformedPosts = posts.map((post: any) => ({
      ...post,
      readingTime: typeof post.readingTime === 'object'
        ? post.readingTime
        : { text: `${post.readingTime} min read`, minutes: post.readingTime }
    }));

    // Cache the result
    blogIndexCache = transformedPosts;

    return transformedPosts;
  } catch (error) {
    console.error('Error loading blog index:', error);
    return [];
  }
}

export async function getPostBySlugSSG(slug: string): Promise<BlogPost | null> {
  // Check cache first
  if (postCache.has(slug)) {
    return postCache.get(slug)!;
  }

  try {
    const response = await fetch(`${BLOG_DATA_BASE}/${slug}.json`);

    if (!response.ok) {
      console.error(`Failed to fetch post ${slug}: ${response.status}`);
      return null;
    }

    const post = await response.json();

    // Transform reading time if needed
    const transformedPost = {
      ...post,
      readingTime: typeof post.readingTime === 'object'
        ? post.readingTime
        : { text: `${post.readingTime} min read`, minutes: post.readingTime }
    };

    // Cache the post
    postCache.set(slug, transformedPost);

    return transformedPost;
  } catch (error) {
    console.error(`Error loading post ${slug}:`, error);
    return null;
  }
}

export async function getPostsByCategorySSG(category: BlogCategory): Promise<BlogPost[]> {
  const posts = await getAllPostsSSG();
  return posts.filter(post => post.category === category);
}

export async function getFeaturedPostsSSG(): Promise<BlogPost[]> {
  const posts = await getAllPostsSSG();
  return posts.filter(post => post.featured);
}

export async function getRelatedPostsSSG(currentPost: BlogPost, limit: number = 3): Promise<BlogPost[]> {
  const posts = await getAllPostsSSG();

  // Filter out current post
  const otherPosts = posts.filter(post => post.slug !== currentPost.slug);

  // Score posts based on similarity
  const scoredPosts = otherPosts.map(post => {
    let score = 0;

    // Same category = 10 points
    if (post.category === currentPost.category) score += 10;

    // Shared tags = 5 points per tag
    const sharedTags = post.tags.filter(tag => currentPost.tags.includes(tag));
    score += sharedTags.length * 5;

    return { post, score };
  });

  // Sort by score and return top matches
  return scoredPosts
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(item => item.post);
}

// Prefetch blog data for better performance
export async function prefetchBlogData() {
  // Prefetch the index
  await getAllPostsSSG();

  // Optionally prefetch featured posts
  const posts = await getAllPostsSSG();
  const featuredPosts = posts.filter(p => p.featured);

  // Prefetch content for featured posts
  for (const post of featuredPosts) {
    await getPostBySlugSSG(post.slug);
  }
}

// Helper to clear cache (useful for development)
export function clearBlogCache() {
  blogIndexCache = null;
  postCache.clear();
}